import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, AzureResource, HealthStatus } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService, MonitoringFinding, MonitoringResourceType } from '../../core/services/monitoring.service';
import { serviceCardById } from '../../core/cloud-catalog';
import { locationLabel } from '../../core/azure-locations';
import { ALL_REGIONS_SCOPE } from '../../core/aws-regions';
import { CloudFilters, FacetDef, SortOption, parseFiltersFromParams, filtersToParams } from './cloud-filter.model';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';
import { CloudTab } from './cloud-tabs.component';

type Tab = 'overview' | 'resources' | 'activity';

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['status', 'location', 'monitoring'];
const DAY_MS       = 24 * 60 * 60 * 1000;

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'issues-desc',     label: 'Most issues'     },
  { value: 'location-asc',    label: 'Location A → Z'  },
  { value: 'monitored-first', label: 'Monitored first' },
];

/**
 * Azure service detail page — the Azure analog of the AWS `/cloud/:service`
 * detail pages, for every Azure service (one generic component, selected by
 * route `data.id`). It mirrors the AWS Lambda page's information architecture
 * and reuses the SAME shared building blocks (cloud-filter-bar toolbar,
 * cloud-tabs, cloud-activity-feed, status-badge, cloudDate, the cloud-filter /
 * cloud-activity models) so AWS and Azure read as one product system.
 *
 * Native to Azure: resources carry a resource group + Azure location + kind/sku,
 * global services (Front Door) are discovered subscription-wide, and the scope
 * is a subscription + location (not an account + region).
 */
@Component({
  selector: 'app-azure-service-detail',
  templateUrl: './azure-service-detail.component.html',
})
export class AzureServiceDetailComponent implements OnInit, OnDestroy {
  id = '';
  serviceType = '';
  title = 'Azure service';
  icon = 'cloud';
  metricLabel = 'Resources';
  isGlobal = false;

  /** Active scope, inherited from the overview via ?account= / ?region=. */
  subscription = '';
  subscriptionLabel = '';
  location = ALL_REGIONS_SCOPE;

  selectedTab: Tab = 'overview';
  resources: AzureResource[] = [];
  loading = true;
  error: string | null = null;
  refreshing = false;

  findings: MonitoringFinding[] = [];
  activity: ActivityItem[] = [];
  activityLoading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };
  readonly pageSize = 10;
  currentPage = 1;
  readonly sortOptions = SORT_OPTIONS;

  /** Selected resource for the side detail panel (?resource=). */
  selectedResourceId: string | null = null;

  readonly toggling = new Set<string>();
  toggleError: string | null = null;
  readonly locationLabel = locationLabel;

  get tabs(): CloudTab[] {
    return [
      { id: 'overview',  label: 'Overview'      },
      { id: 'resources', label: this.metricLabel },
      { id: 'activity',  label: 'Activity'      },
    ];
  }

  private qpSub?: Subscription;
  private loadSub?: Subscription;
  private connSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.data['id'] as string;
    const card = serviceCardById(id);
    this.id = id;
    this.serviceType = card?.serviceType ?? '';
    this.title = card?.name ?? 'Azure service';
    this.icon = card?.icon ?? 'cloud';
    this.metricLabel = card?.metricLabel ?? 'Resources';
    this.isGlobal = card?.scope === 'global';

    this.cloud.loadMonitoringState().subscribe();
    this.applyScope();
    this.resolveSubscriptionLabel();
    this.loadData(false);
    this.loadFindings();

    this.qpSub = this.route.queryParams.subscribe(params => {
      const newFilters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      if (JSON.stringify(newFilters) !== JSON.stringify(this.filters)) this.currentPage = 1;
      this.filters = newFilters;
      this.selectedResourceId = (params['resource'] as string | undefined) ?? null;

      const changed = this.applyScope();
      if (changed) { this.resolveSubscriptionLabel(); this.loadData(false); this.loadFindings(); }
    });
  }

  /** Reads the subscription + location from the URL and sets the shared scope. Returns true on change. */
  private applyScope(): boolean {
    const account = this.route.snapshot.queryParamMap.get('account') ?? this.cloud.getAccount();
    const region = this.route.snapshot.queryParamMap.get('region') ?? ALL_REGIONS_SCOPE;
    const changed = account !== this.subscription || region !== this.location;
    this.subscription = account;
    this.location = region;
    this.cloud.setAccount(account);
    this.cloud.setScope(region);
    return changed;
  }

  private resolveSubscriptionLabel(): void {
    this.subscriptionLabel = this.subscription;
    this.connSub?.unsubscribe();
    this.connSub = this.cloud.getAzureConnection().subscribe(conn => {
      const sub = conn?.subscriptions.find(s => s.subscriptionId === this.subscription);
      this.subscriptionLabel = sub?.label || this.subscription;
    });
  }

  private loadData(bypass: boolean): void {
    this.loading = !bypass;
    this.refreshing = bypass;
    this.loadSub?.unsubscribe();
    this.loadSub = this.cloud.getAzureResourcesResult(this.serviceType).subscribe(res => {
      this.resources = res.resources;
      this.error = res.error;
      this.loading = false;
      this.refreshing = false;
    });
  }

  private loadFindings(): void {
    this.activityLoading = true;
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(all => {
      this.findings = all.filter(f => f.serviceType === (this.serviceType as MonitoringResourceType));
      this.activity = sortActivity(findingsToActivity(all, this.serviceType as MonitoringResourceType));
      this.activityLoading = false;
    });
  }

  refresh(): void {
    if (this.refreshing) return;
    this.loadData(true);
    this.loadFindings();
  }

  // ── Scope labels ──────────────────────────────────────────────────────────

  get locationScopeLabel(): string {
    if (this.isGlobal) return 'Global';
    if (this.location === ALL_REGIONS_SCOPE) return 'All locations';
    return this.locationLabel(this.location);
  }

  // ── Summary counts (Overview) ──────────────────────────────────────────────

  get totalCount(): number { return this.resources.length; }
  get monitoredCount(): number { return this.resources.filter(r => this.isMonitored(r)).length; }
  get distinctLocationCount(): number { return new Set(this.resources.map(r => r.location)).size; }
  get issues24hTotal(): number {
    const cutoff = this.now() - DAY_MS;
    return this.findings.filter(f => Date.parse(f.createdAt) >= cutoff).length;
  }

  /** Open findings for a resource within the last 24h (drives the Issues column). */
  issueCount(resourceId: string): number {
    const cutoff = this.now() - DAY_MS;
    return this.findings.filter(f => f.resourceId === resourceId && Date.parse(f.createdAt) >= cutoff).length;
  }

  /** Highest severity among a resource's recent findings ('' when none). */
  private now(): number { return Date.parse(new Date().toISOString()); }

  // ── Status mapping (Azure provisioning/power state → shared health badge) ──

  statusHealth(r: AzureResource): HealthStatus {
    const s = (r.status || '').toLowerCase();
    if (['succeeded', 'running', 'online', 'ready', 'available', 'active'].includes(s)) return 'healthy';
    if (['failed', 'canceled', 'cancelled', 'stopped', 'deallocated'].includes(s)) return 'critical';
    if (['creating', 'updating', 'deleting', 'provisioning', 'starting'].includes(s)) return 'warning';
    return 'unknown';
  }

  /** Kind or SKU — the Azure-native "plan/type" signal, whichever the resource carries. */
  kindOrSku(r: AzureResource): string { return r.kind || r.sku || '—'; }

  // ── Facets (dynamic: locations + statuses derived from data) ───────────────

  private _facetsRef: AzureResource[] | null = null;
  private _facetsCache: FacetDef[] = [];

  get facets(): FacetDef[] {
    if (this._facetsRef === this.resources) return this._facetsCache;
    this._facetsRef = this.resources;
    const locations = [...new Set(this.resources.map(r => r.location))].sort();
    const statuses  = [...new Set(this.resources.map(r => this.statusHealth(r)))];
    const STATUS_LABELS: Record<string, string> = { healthy: 'Healthy', warning: 'Transitional', critical: 'Not running', unknown: 'Unknown', idle: 'Idle' };
    this._facetsCache = [
      { key: 'status',   label: 'Status',   options: statuses.map(s => ({ value: s, label: STATUS_LABELS[s] ?? s })) },
      { key: 'location', label: 'Location', options: locations.map(l => ({ value: l, label: this.locationLabel(l) !== l ? `${l} (${this.locationLabel(l)})` : l })) },
      { key: 'monitoring', label: 'Monitoring', options: [
        { value: 'monitored',   label: 'Monitored' },
        { value: 'unmonitored', label: 'Not monitored' },
      ] },
    ];
    return this._facetsCache;
  }

  // ── Filtered + sorted list ─────────────────────────────────────────────────

  get filteredResources(): AzureResource[] {
    let result = this.resources;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(r => r.name.toLowerCase().includes(q) || (r.resourceGroup ?? '').toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(r => statuses.includes(this.statusHealth(r)));

    const locations = this.filters.facets['location'] ?? [];
    if (locations.length) result = result.filter(r => locations.includes(r.location));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(r => {
        const mon = this.isMonitored(r);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }
    return this.sorted(result, this.filters.sort);
  }

  private sorted(rs: AzureResource[], sort: string): AzureResource[] {
    return [...rs].sort((a, b) => {
      switch (sort) {
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'location-asc':  return a.location.localeCompare(b.location) || a.name.localeCompare(b.name);
        case 'issues-desc':   return this.issueCount(b.id) - this.issueCount(a.id) || a.name.localeCompare(b.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a) ? 0 : 1;
          const bM = this.isMonitored(b) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default:              return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.currentPage = 1;
    this.router.navigate([], { relativeTo: this.route, queryParams: filtersToParams(f, FACET_KEYS), queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  get totalPages(): number { return Math.max(1, Math.ceil(this.filteredResources.length / this.pageSize)); }
  get pageNumbers(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
  get pagedResources(): AzureResource[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredResources.slice(start, start + this.pageSize);
  }
  get pageStart(): number { return this.filteredResources.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; }
  get pageEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredResources.length); }
  goToPage(p: number): void { this.currentPage = Math.max(1, Math.min(p, this.totalPages)); }
  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }

  // ── Monitoring ─────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(r: AzureResource): boolean {
    return this.cloud.isAzureMonitored(this.serviceType, r.id, this.subscription, this.isGlobal ? undefined : this.location);
  }
  isToggling(r: AzureResource): boolean { return this.toggling.has(r.id); }

  toggle(r: AzureResource): void {
    if (this.toggling.has(r.id)) return;
    const next = !this.isMonitored(r);
    if (next && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    const loc = this.isGlobal ? undefined : (r.location || (this.location !== ALL_REGIONS_SCOPE ? this.location : undefined));
    this.toggling.add(r.id);
    this.toggleError = null;
    this.cloud.toggleAzureMonitoring(this.serviceType, r.id, next, loc, { name: r.name }).subscribe({
      next: () => { this.toggling.delete(r.id); },
      error: (err: { status?: number; error?: { code?: string; message?: string } }) => {
        this.toggling.delete(r.id);
        if (err.status === 403) {
          this.toggleError = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
        } else {
          this.toggleError = err?.error?.message ?? 'Failed to update monitoring for this resource.';
        }
      },
    });
  }

  // ── Side detail panel ──────────────────────────────────────────────────────

  selectResource(id: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { resource: id }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { resource: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  get selectedResource(): AzureResource | null {
    return this.resources.find(r => r.id === this.selectedResourceId) ?? null;
  }
  get selectedResourceFindings(): MonitoringFinding[] {
    if (!this.selectedResourceId) return [];
    return this.findings
      .filter(f => f.resourceId === this.selectedResourceId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'resources';
    this.selectResource(item.resourceId);
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.connSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }
}
